import React, { useState } from 'react';
import { Tabs, Card, Form, Input, InputNumber, Select, Button, Table, Switch, message, Row, Col } from 'antd';
import { FileTextOutlined, DollarOutlined, FileSearchOutlined, PlusOutlined } from '@ant-design/icons';
import './SuperAdminStyles.css';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const Estimates = () => {
  const [activeTab, setActiveTab] = useState('builder');
  const [form] = Form.useForm();
  const [proximityEnabled, setProximityEnabled] = useState(false);

  const handleGeneratePDF = () => {
    message.success('PDF proposal generated successfully');
  };

  const handleSaveEstimate = () => {
    message.success('Estimate saved successfully');
  };

  const pendingColumns = [
    { title: 'Client', dataIndex: 'client', key: 'client' },
    { title: 'Service', dataIndex: 'service', key: 'service' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `$${val}` },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Date', dataIndex: 'date', key: 'date' },
  ];

  const invoiceColumns = [
    { title: 'Invoice #', dataIndex: 'invoice', key: 'invoice' },
    { title: 'Client', dataIndex: 'client', key: 'client' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `$${val}` },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Due Date', dataIndex: 'dueDate', key: 'dueDate' },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Estimate Builder</h2>
        <p style={{ color: '#6b7280', margin: '4px 0 0 0' }}>Create pricing estimates for prospects and clients</p>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Estimate Builder
            </span>
          }
          key="builder"
        >
          <Row gutter={16}>
            <Col xs={24} lg={16}>
              {/* Client Information */}
              <Card title="Client Information" style={{ marginBottom: '16px' }}>
                <Form form={form} layout="vertical">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Company Name *" name="companyName">
                        <Input placeholder="Acme Car Wash Inc." />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Contact Name" name="contactName">
                        <Input placeholder="John Smith" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Email" name="email">
                        <Input placeholder="john@acmecarwash.com" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Phone" name="phone">
                        <Input placeholder="(555) 123-4567" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Notes" name="notes">
                    <TextArea rows={3} placeholder="Additional notes for this proposal..." />
                  </Form.Item>
                </Form>
              </Card>

              {/* RouteIQ - Fleet Tracking */}
              <Card title="RouteIQ - Fleet Tracking" extra={<span style={{ color: '#3b82f6' }}>Required</span>} style={{ marginBottom: '16px' }}>
                <Form layout="vertical">
                  <Form.Item label="Number of Locations">
                    <Select defaultValue="enterprise" style={{ width: '200px' }}>
                      <Option value="single">Single Location</Option>
                      <Option value="enterprise">Enterprise (3+)</Option>
                    </Select>
                  </Form.Item>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Price Range</div>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>Large</div>
                        <div>$10-$15/mo</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>Custom</div>
                        <div>$0-$5 25/mo</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>Custom</div>
                        <div>$5-$10/mo</div>
                      </div>
                    </div>
                  </div>
                  <Form.Item label="Number of Vehicles">
                    <Row gutter={8}>
                      <Col span={8}>
                        <Select defaultValue="custom" style={{ width: '100%' }}>
                          <Option value="custom">Custom (1+)</Option>
                        </Select>
                      </Col>
                      <Col span={8}>
                        <InputNumber min={1} defaultValue={10} style={{ width: '100%' }} />
                      </Col>
                    </Row>
                  </Form.Item>
                </Form>
              </Card>

              {/* ProximityIQ - Proximity Marketing */}
              <Card 
                title="ProximityIQ - Proximity Marketing" 
                extra={
                  <Switch 
                    checked={proximityEnabled} 
                    onChange={setProximityEnabled}
                    checkedChildren="Optional"
                    unCheckedChildren="Optional"
                  />
                } 
                style={{ marginBottom: '16px' }}
              >
                {proximityEnabled && (
                  <div>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                      <strong>Conditional Pricing:</strong> When ProximityIQ is enabled, vehicle pricing is automatically adjusted to include both RouteIQ and ProximityIQ proximity marketing features in one simple price.
                    </p>
                    <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '6px' }}>
                      <h4 style={{ margin: '0 0 12px 0' }}>SMS/Email Package</h4>
                      <Row gutter={16}>
                        <Col span={8}>
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>Name</div>
                            <div>PAYG</div>
                          </div>
                        </Col>
                        <Col span={8}>
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>Pay As You Go</div>
                            <div>$0.05</div>
                          </div>
                        </Col>
                        <Col span={8}>
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>Starter</div>
                            <div>$25</div>
                          </div>
                        </Col>
                      </Row>
                      <div style={{ marginTop: '12px', fontSize: '12px' }}>1 text: 3 locations = $30 = $10/mo/mo</div>
                    </div>
                  </div>
                )}
              </Card>

              {/* Add-ons & Discounts */}
              <Card title="Add-ons & Discounts" style={{ marginBottom: '16px' }}>
                <Form layout="vertical">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Setup Fee (One-time)">
                        <InputNumber prefix="$" defaultValue={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Discount">
                        <InputNumber prefix="$" defaultValue={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Custom Line Items">
                    <Button icon={<PlusOutlined />} type="dashed" block>
                      Add Item
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              {/* Estimate Summary */}
              <Card title="Estimate Summary" style={{ marginBottom: '16px', position: 'sticky', top: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Fleet - ProximityIQ</span>
                    <span>$990</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', color: '#6b7280' }}>
                    <span>Locations (3 x $0)</span>
                    <span>$0</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Subtotal</span>
                    <span>$990</span>
                  </div>
                  <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>SMS & Email Credits</span>
                    <span></span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', color: '#6b7280' }}>
                    <span>Growth (10 x $50)</span>
                    <span>$152</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Subtotal</span>
                    <span>$152</span>
                  </div>
                  <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                    <span>Monthly Total</span>
                    <span>$1,140</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>plus tax where applicable</div>
                </div>

                <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '13px' }}>Variable Margin Analysis</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span>Variable cost (10 x $0)</span>
                    <span>$5</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span>Ad. GBP/Meta</span>
                    <span>$3</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '8px' }}>
                    <span>Total Cost</span>
                    <span>$24</span>
                  </div>
                  <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #d1d5db' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold', color: '#10b981' }}>
                    <span>Gross Margin</span>
                    <span>$1,116 (97.9%)</span>
                  </div>
                </div>

                <Button type="primary" block size="large" onClick={handleGeneratePDF} style={{ marginBottom: '8px' }}>
                  Generate PDF Proposal
                </Button>
                <Button block onClick={handleSaveEstimate}>
                  Save Estimate
                </Button>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <DollarOutlined />
              Pending Pricing Fees
            </span>
          }
          key="pending"
        >
          <Card>
            <Table 
              columns={pendingColumns} 
              dataSource={[]} 
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No pending pricing fees' }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileSearchOutlined />
              Invoices/Conversion Charges
            </span>
          }
          key="invoices"
        >
          <Card>
            <Table 
              columns={invoiceColumns} 
              dataSource={[]} 
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No invoices or conversion charges' }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Estimates;
